import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type GridPosition } from '../../../machine/state/types';
import { sumSelectedRowSpan } from '../../../machine/utils/sum-selected-row-span';
import { type ClickTarget } from '../utils/get-click-target';

export interface SelectionModifiers {
  extend?: boolean;
  toggle?: boolean;
  getVisibleEntryCount: () => number;
  /** Same notion as viewport `getMaxVisibleRowIndex` (last selectable row index). */
  getMaxVisibleRowIndex: () => number;
  /**
   * Invoked synchronously immediately before `selectCell` is dispatched (plain cell click path only).
   * Used to anchor stabilized metrics at selection commit, not pointer-up.
   */
  onSimpleCellSelectDispatch?: (startTime: number, position: GridPosition) => void;
}

export function dispatchInitialClick(
  machine: SelectionsMachine,
  target: ClickTarget,
  modifiers: SelectionModifiers
): void {
  const { extend = false, getVisibleEntryCount, getMaxVisibleRowIndex } = modifiers;
  const state = machine.getState();

  if (target.type === 'headerCheckbox') {
    const visibleEntryCount = getVisibleEntryCount();
    const fullySelected =
      visibleEntryCount > 0 &&
      state.mode === 'row' &&
      sumSelectedRowSpan(state) === visibleEntryCount;

    if (fullySelected) {
      machine.dispatch({ type: 'deselectAll', source: 'mouse' });
    } else if (visibleEntryCount > 0) {
      machine.dispatch({
        type: 'selectRows',
        source: 'mouse',
        ranges: [
          {
            anchor: { row: { index: 0 }, col: { index: 0 } },
            lead: {
              row: { index: getMaxVisibleRowIndex() },
              col: { index: 0 }
            }
          }
        ],
        focusRow: { index: 0 }
      });
    }
  } else if (target.type === 'cell') {
    if (extend && state.mode === 'cell' && state.selections.length === 1) {
      machine.dispatch({
        type: 'extendToCell',
        cell: target.position,
        source: 'mouse'
      });
    } else {
      const { onSimpleCellSelectDispatch } = modifiers;

      if (onSimpleCellSelectDispatch) {
        onSimpleCellSelectDispatch(performance.now(), target.position);
      }

      machine.dispatch({ type: 'selectCell', cell: target.position, source: 'mouse' });
    }
  } else if (target.type === 'row') {
    if (extend && state.focusedCell != null) {
      machine.dispatch({
        type: 'extendToRow',
        row: target.row,
        source: 'mouse'
      });
    } else if (target.source === 'row-handle') {
      if (
        state.mode === 'row' &&
        state.selections.length > 0 &&
        state.focusedCell?.row.index === target.row.index
      ) {
        return;
      }

      machine.dispatch({
        type: 'selectRows',
        ranges: [
          {
            anchor: {
              row: target.row,
              col: {
                index: 0
              }
            },
            lead: {
              row: target.row,
              col: {
                index: 0
              }
            }
          }
        ],
        source: 'mouse'
      });
    } else {
      machine.dispatch({ type: 'toggleRow', row: target.row, source: 'mouse' });
    }
  }
}
